import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, ChevronRight } from "lucide-react";
import { URUT_PRODI, kelompokkanRincian, perluKelompok } from "@/lib/kelompokProdi";
import { segmenSedangBerjalan, teksPeriodeSegmen } from "@/lib/segmenRotasi";

// Periode = rentang (Stase) atau daftar tanggal Jaga 1 orang ("1, 5, 7 Okt") + jumlah hari di bulan ini.
function SelPeriode({ d }) {
    const teks = teksPeriodeSegmen(d);
    if (!teks) return <span className="text-slate-400">-</span>;
    return (
        <span className="inline-flex items-center gap-1.5">
            {teks}
            {segmenSedangBerjalan(d) && (
                <Badge className="bg-[#CDDC29] text-[#0F2E30] hover:bg-[#CDDC29] text-[10px] px-1.5 py-0">
                    Sedang Berjalan
                </Badge>
            )}
        </span>
    );
}

function BarisPpds({ d, isInstalasi, denganProdi, indent = "pl-10" }) {
    return (
        <tr className="border-t border-slate-200" data-testid="rk-inst-rincian-baris">
            <td className={`${indent} font-semibold text-slate-800`}>{d.nama}</td>
            {!isInstalasi && <td className="font-mono text-xs">{d.npm}</td>}
            {denganProdi && <td className="text-xs text-slate-600">{d.prodi_nama}</td>}
            <td><Badge variant="outline" className="text-[10px]">{d.jenis}</Badge></td>
            <td className="pr-4 text-xs text-slate-600"><SelPeriode d={d} /></td>
        </tr>
    );
}

// Tampilan lama (satu Program Studi saja, mis. Admin Prodi): daftar urut nama, tanpa kelompok.
function RincianDatar({ row, isInstalasi, istilah }) {
    return (
        <table className="w-full compact">
            <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    <th className="pl-10">Nama</th>{!isInstalasi && <th>NPM</th>}
                    <th>{istilah.panjang}</th><th>Jenis</th><th className="pr-4">Periode</th>
                </tr>
            </thead>
            <tbody>
                {[...row.rincian].sort((a, b) => a.nama.localeCompare(b.nama, "id")).map((d, i) => (
                    <BarisPpds key={`${d.peserta_id ?? d.nama}-${d.jenis}-${i}`} d={d} isInstalasi={isInstalasi} denganProdi />
                ))}
                {row.rincian.length === 0 && (
                    <tr><td colSpan={isInstalasi ? 4 : 5} className="pl-10 py-3 text-slate-400 text-xs">Tidak ada rincian.</td></tr>
                )}
            </tbody>
        </table>
    );
}

// Rincian satu instalasi yang dibuka. Lebih dari satu Program Studi -> dikelompokkan per prodi
// (judul berisi PPDS Stase/Jaga/Total dari server, klik untuk membuka nama-namanya).
// istilah: istilah prodi per peran (istilahProdi di lib/roles.js; Admin Koas -> "Modul"). Bawaan "Program Studi".
const ISTILAH_BAWAAN = { singkat: "Prodi", panjang: "Program Studi", kecil: "prodi" };
export default function RincianInstalasi({ row, isInstalasi, showStase, showJaga, istilah = ISTILAH_BAWAAN }) {
    const [urut, setUrut] = useState("nama");
    const [terbuka, setTerbuka] = useState(() => new Set());
    const kelompok = useMemo(() => kelompokkanRincian(row, urut), [row, urut]);

    if (!perluKelompok(row)) return <RincianDatar row={row} isInstalasi={isInstalasi} istilah={istilah} />;

    const kolomNama = isInstalasi ? 1 : 2; // Nama (+ NPM)
    const kolom = kolomNama + 2; // + Jenis + Periode
    const toggle = (label) => setTerbuka((s) => {
        const baru = new Set(s);
        if (baru.has(label)) baru.delete(label); else baru.add(label);
        return baru;
    });
    const semuaTerbuka = kelompok.length > 0 && kelompok.every((k) => terbuka.has(k.prodi_label));

    return (
        <div data-testid="rk-inst-kelompok">
            <div className="pl-10 pr-4 py-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                <span className="font-semibold">{kelompok.length} {istilah.panjang}</span>
                <Button
                    type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid="rk-inst-buka-semua"
                    onClick={() => setTerbuka(semuaTerbuka ? new Set() : new Set(kelompok.map((k) => k.prodi_label)))}
                >
                    {semuaTerbuka ? "Tutup semua" : "Buka semua"}
                </Button>
                <span className="ml-auto inline-flex items-center gap-1.5">
                    <span>Urut:</span>
                    <Select value={urut} onValueChange={setUrut}>
                        <SelectTrigger className="h-7 w-40 text-xs" aria-label={`Urutan ${istilah.panjang}`} data-testid="rk-inst-urut">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {URUT_PRODI.map((o) => <SelectItem key={o.value} value={o.value}>{o.value === "nama" ? `Nama ${istilah.singkat}` : o.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </span>
            </div>
            <p className="pl-10 pr-4 pb-2 text-[11px] text-slate-500">
                Angka = PPDS unik per jenis. Total = Stase + Jaga, jadi PPDS yang Stase dan Jaga sekaligus di instalasi ini terhitung di keduanya.
            </p>
            <table className="w-full compact">
                <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                        <th className="pl-10">{istilah.panjang} / Nama</th>
                        {!isInstalasi && <th>NPM</th>}
                        <th>Jumlah / Jenis</th>
                        <th className="pr-4">Total / Periode</th>
                    </tr>
                </thead>
                <tbody>
                    {kelompok.map((k) => {
                        const buka = terbuka.has(k.prodi_label);
                        const angka = [showStase && `Stase ${k.jumlah_stase}`, showJaga && `Jaga ${k.jumlah_jaga}`].filter(Boolean).join(" · ");
                        return [
                            <tr key={k.prodi_label} className="border-t border-slate-200 bg-slate-100" data-testid="rk-inst-prodi">
                                <td colSpan={kolomNama} className="pl-6">
                                    <button
                                        type="button" aria-expanded={buka}
                                        className="inline-flex items-center gap-1.5 font-semibold text-slate-800 text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00B9AD] rounded"
                                        onClick={() => toggle(k.prodi_label)} data-testid="rk-inst-prodi-toggle"
                                    >
                                        {buka ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                                        {k.prodi_label}
                                    </button>
                                </td>
                                <td className="text-xs text-slate-600">{angka}</td>
                                <td className="pr-4 font-bold text-slate-900" data-testid="rk-inst-prodi-total">{k.total}</td>
                            </tr>,
                            ...(buka ? k.rincian.map((d, i) => (
                                <BarisPpds key={`${k.prodi_label}-${d.peserta_id ?? d.nama}-${d.jenis}-${i}`} d={d} isInstalasi={isInstalasi} indent="pl-14" />
                            )) : []),
                        ];
                    })}
                    {kelompok.length === 0 && (
                        <tr><td colSpan={kolom} className="pl-10 py-3 text-slate-400 text-xs">Tidak ada rincian.</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}
